import { getReportStatus } from '../../features/queries/getReportStatus';
import { shortId } from '../../lib/ids';
import { useQ } from '../../lib/query';
import VoteCardActions from '../../pages/podnety/[id]/VoteCardActions';
import { __, copyToClipboard, formatDateTime } from '../../utils';
import ErbThumbnail from '../ErbThumbnail';
import type { IFeedItem } from '../FeedItem/FeedItem';
import { FormattedText } from '../FormattedText';
import LoginModal from '../LoginModal';
import { reverseGeocode } from '../MapSearch/arcgisSearch';
import PageMeta from '../PageMeta';
import ReportDocuments from './ReportDocuments';
import ReportPhotos from './ReportPhotos';
import ReportStatements from './ReportStatements';
import { feedItemBadge } from './feedStatus';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NorthIcon from '@mui/icons-material/North';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  Stack,
  StatusChip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { Link } from 'react-router';

/** Ako dlho drží tlačidlo ID nápis „Skopírované". Zo zadania: 1,6 s. */
const COPIED_MS = 1600;

type Props = {
  item: IFeedItem;
  /** Pohľad mapy - z neho sa berie geokóder na adresu. */
  view?: __esri.MapView | null;
  /** Mobilný sheet: nižšia hlavná fotka a hustejšie odsadenie. */
  compact?: boolean;
  onBack: () => void;
  /** Text tlačidla späť. Z mapy sa vracia na zoznam, z „Mojich podnetov" tam. */
  backLabel?: string;
};

/**
 * Detail položky **v paneli**, nie na vlastnej stránke a nie ako bublina pri
 * značke. Nahradil `MapItemDetail`, ktorý kreslil to isté na dvoch miestach
 * naraz (bublina na desktope, modálny sheet na mobile) a v oboch sa doň
 * zmestili tri riadky popisu. Od augusta 2026 je to **jediné** miesto, kde sa
 * detail podnetu kreslí: samostatná stránka `/podnety/:reportId` zanikla
 * a presmerúva sem ([[ReportDetailRedirect]]).
 *
 * Poradie zo zadania je záväzné: fotky, riadok adresa - dátum, názov, popis,
 * čiara, vyjadrenia úradu po kategóriách, pätička s podporou a zdieľaním.
 * **Stavový štítok nad tým nie je** - stav hovoria karty kategórií nižšie,
 * a to presnejšie, než by ho povedal jeden chip za celý podnet.
 *
 * Dáta sa neťahajú znova: hlavička, fotky, názov aj popis sú tá istá
 * `IFeedItem`, ktorú už drží feed - preto v nej `getFeed` nesie celé `media`.
 * Dotaz ide len na to, čo v nej nie je: vyjadrenia úradu (`getReportStatus`)
 * a adresa (reverzný geokóder).
 */
const FeedDetail: React.FC<Props> = ({
  item,
  view,
  compact = false,
  onBack,
  backLabel,
}) => {
  const isNews = item.type === 'news';
  const isReport = !isNews;
  /**
   * Vlastný podnet sa podporiť nedá - hlas je prejav toho, že záležitosť
   * páli aj niekoho iného, a podanie samo o sebe je už najsilnejší možný
   * prejav autora. `userReport` je typ, ktorý položke dáva [[getFeed]] podľa
   * toho, či prišla z `/me/reports`; panel v profile ho nesie natvrdo, lebo
   * tam je vlastníctvo isté. Posledné slovo má aj tak API - `POST
   * /me/vote-tokens` hlas na vlastný podnet odmietne.
   */
  const isOwnReport = item.type === 'userReport';
  const badge = feedItemBadge(item);

  const [address, setAddress] = React.useState<string>();
  const [copiedId, setCopiedId] = React.useState(false);
  const [copiedLink, setCopiedLink] = React.useState(false);
  const [loginOpen, setLoginOpen] = React.useState(false);

  const { data: statuses = [], isLoading: isLoadingStatuses } = useQ(
    getReportStatus,
    { reportId: item.id },
    { enabled: isReport },
  );

  const coordinates = item.location?.coordinates;

  React.useEffect(() => {
    let cancelled = false;
    setAddress(undefined);

    if (!coordinates) return;

    reverseGeocode(coordinates, view)
      .then((value) => {
        if (!cancelled) setAddress(value);
      })
      // Bez odpovede sa riadok jednoducho nevykreslí. Adresa je príjemnosť,
      // nie údaj, bez ktorého by detail nedával zmysel - polohu nesie mapa.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [coordinates, view]);

  const code = shortId(item.id);
  // Zdieľa sa odkaz na **položku**, nie na hlavnú stránku - adresa
  // v prehliadači je mapa a tá by príjemcovi nič nepovedala.
  //
  // Podnet má adresu `/podnety/<ID>` - tú istú, na ktorej ho mapa otvára
  // ([[HomePage]]). Tú cestu nginx posiela do digiobcan-api, ktoré do
  // `index.html` doplní názov, popis a fotku podnetu - náhľad pre Facebook
  // a WhatsApp, ktoré JavaScript nespúšťajú.
  const publicPath = isNews ? `/novinky/${code}` : `/podnety/${code}`;

  // Novinka nesie jednu titulnú fotku, podnet celý pás príloh z feedu.
  const media = React.useMemo(
    () =>
      item.media?.length
        ? item.media
        : item.image
          ? [{ url: item.image, type: 'image/*' }]
          : item.video
            ? [{ url: item.video, type: 'video/*' }]
            : [],
    [item.media, item.image, item.video],
  );

  return (
    <Stack sx={{ height: 1, minHeight: 0, gap: 1.25 }}>
      {/* Otvorený detail je pre prehliadač vlastná stránka - titul záložky
          a popis sa vrátia na hlavnú stránku, keď sa panel zavrie. */}
      <PageMeta
        title={`${isNews ? __('Novinka') : __('Podnet')}: ${item.title}`}
        url={publicPath}
        description={item.description}
        image={item.image}
        type='article'
      />
      <Stack
        direction='row'
        sx={{ flex: 'none', alignItems: 'center', gap: 1 }}
      >
        {/* Pilulka na tichej ploche, nie textové tlačidlo: v dizajne panela je
            to jediný ovládací prvok nad obsahom, takže musí byť vidieť ako
            tlačidlo. Odtiaľ sú aj rozmery: výška 32, rádius plný, plocha
            `grey[100]`, hover `grey[200]`. */}
        <ButtonBase
          onClick={onBack}
          sx={(theme) => ({
            flex: 'none',
            alignItems: 'center',
            gap: 0.875,
            height: theme.typography.pxToRem(32),
            padding: theme.spacing(0, 1.25, 0, 0.75),
            borderRadius: `${theme.shape.borderRadiusFull}px`,
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.grey[700],
            fontSize: theme.typography.pxToRem(13),
            fontWeight: 700,
            transition: 'background-color 120ms',
            '&:hover': { backgroundColor: theme.palette.grey[200] },
          })}
        >
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
          {backLabel ?? __('Späť na zoznam')}
        </ButtonBase>

        {/* ID vpravo hore je **tlačidlo**: skopírovať kód je najčastejšia vec,
            ktorú občan pri podnete potrebuje - hovorí ho do telefónu na úrad. */}
        <ButtonBase
          onClick={() =>
            copyToClipboard(code, {
              onSuccess: () => {
                setCopiedId(true);
                window.setTimeout(() => setCopiedId(false), COPIED_MS);
              },
            })
          }
          title={__('Kopírovať ID podnetu')}
          sx={(theme) => ({
            marginLeft: 'auto',
            flex: 'none',
            alignItems: 'center',
            gap: 0.75,
            height: theme.typography.pxToRem(28),
            padding: theme.spacing(0, 1.125),
            borderRadius: `${theme.shape.borderRadiusFull}px`,
            border: `${theme.typography.pxToRem(1)} solid ${
              copiedId ? theme.palette.green.light : theme.palette.divider
            }`,
            backgroundColor: copiedId
              ? theme.palette.green.surface
              : theme.palette.background.paper,
            color: copiedId
              ? theme.palette.green.dark
              : theme.palette.grey[500],
            fontSize: theme.typography.pxToRem(12),
            fontFamily: copiedId
              ? theme.typography.fontFamily
              : theme.typography.fontFamilyMono,
          })}
        >
          {copiedId ? __('Skopírované') : code}
          {copiedId ? (
            <CheckIcon sx={{ fontSize: 13 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 13 }} />
          )}
        </ButtonBase>
      </Stack>

      <Stack
        // Ten istý príznak ako má zoznam: mobilný list sa smie zatvoriť ťahom
        // nadol len vtedy, keď je obsah odrolovaný hore ([[FeedSheet]]).
        data-feed-scroll=''
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', gap: 1.5 }}
      >
        {isReport ? (
          <ReportPhotos media={media} title={item.title} compact={compact} />
        ) : media.length ? (
          // Erb namiesto mŕtvej adresy - to isté, čo drží karta v zozname
          // ([[ErbThumbnail]]). Novinky nesú obrázky z cudzích webov, takže
          // 404 je tu bežný stav, nie výnimka, a hláška „Obrázok sa nenašiel"
          // z knižnice o ňom občanovi nič nepovie.
          <ErbThumbnail
            src={media[0].url}
            alt={item.title}
            erbSize={44}
            sx={{ flex: 'none', width: 1, height: 140, borderRadius: 1 }}
          />
        ) : null}

        {/* Podnet: adresa vľavo, dátum podania vpravo - bez slova „Podané"
            a bez stavového chipu. Novinka a podnet v spracovaní stav ešte
            potrebujú, tam štítok zostáva. */}
        {isReport ? (
          <Stack
            direction='row'
            sx={{
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            {address ? (
              <Stack
                direction='row'
                sx={(theme) => ({
                  minWidth: 0,
                  alignItems: 'flex-start',
                  gap: 0.875,
                  fontSize: theme.typography.pxToRem(13),
                  lineHeight: 1.45,
                  color: theme.palette.grey[700],
                })}
              >
                <PlaceOutlinedIcon
                  sx={(theme) => ({
                    flex: 'none',
                    fontSize: 15,
                    marginTop: '1px',
                    color: theme.palette.grey[400],
                  })}
                />
                <Box component='span'>{address}</Box>
              </Stack>
            ) : (
              <Box />
            )}
            {item.date ? (
              <Box
                component='span'
                sx={(theme) => ({
                  flex: 'none',
                  fontSize: theme.typography.pxToRem(12.5),
                  color: theme.palette.grey[500],
                })}
              >
                {formatDateTime(new Date(item.date))}
              </Box>
            ) : null}
          </Stack>
        ) : (
          <Stack
            direction='row'
            sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
          >
            <StatusChip tone={badge.tone}>{badge.label}</StatusChip>
            <Box
              component='span'
              sx={(theme) => ({
                fontSize: theme.typography.pxToRem(11),
                color: theme.palette.grey[500],
              })}
            >
              {`${isNews ? __('Zverejnené') : __('Podané')} ${
                item.date ? formatDateTime(new Date(item.date)) : ''
              }`}
            </Box>
          </Stack>
        )}

        <Box
          component='h2'
          sx={(theme) => ({
            margin: 0,
            fontSize: theme.typography.pxToRem(isReport ? 20 : 17),
            fontWeight: 700,
            lineHeight: 1.25,
            wordBreak: 'break-word',
          })}
        >
          {item.title}
        </Box>

        <Box sx={(theme) => ({ color: theme.palette.grey[700] })}>
          <FormattedText text={item.description ?? ''} />
        </Box>

        {isReport ? <ReportDocuments media={media} /> : null}

        {isReport ? (
          <>
            <Divider sx={{ flex: 'none' }} />
            <ReportStatements
              statuses={statuses}
              isLoading={isLoadingStatuses}
            />
          </>
        ) : null}
      </Stack>

      <Stack
        direction='row'
        sx={{ flex: 'none', gap: 1, alignItems: 'center' }}
      >
        {/* Podnet sa dá podporiť, novinka otvoriť na svojej stránke.
            Podnet vlastnú stránku nemá, takže mu okrem podpory ostáva
            len zdieľanie. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isReport && isOwnReport ? (
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='center'
              gap={0.75}
              sx={{
                height: 44,
                px: 1.5,
                borderRadius: 1,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <NorthIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
              <Typography variant='body2' color='text.secondary'>
                {`${__('Váš podnet')} · ${item.votesCount ?? 0}`}
              </Typography>
            </Stack>
          ) : isReport ? (
            <VoteCardActions
              variant='panel'
              reportId={item.id}
              votesCount={item.votesCount ?? 0}
              onClickUnregisteredUser={() => setLoginOpen(true)}
            />
          ) : isNews ? (
            <Button
              variant='outline'
              size='large'
              fullWidth
              LinkComponent={Link}
              // `to` nie je v type Buttonu z knižnice - prepúšťa len podmnožinu
              // MUI props, hoci do `LinkComponent` ich predá.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...({ to: publicPath } as any)}
            >
              {__('Zobraziť novinku')}
            </Button>
          ) : null}
        </Box>

        <ButtonBase
          aria-label={__('Kopírovať odkaz')}
          onClick={() => {
            copyToClipboard(`${window.location.origin}${publicPath}`, {
              onSuccess: () => {
                setCopiedLink(true);
                window.setTimeout(() => setCopiedLink(false), 2000);
              },
            });
          }}
          sx={(theme) => ({
            flex: 'none',
            width: theme.controlHeight.large,
            height: theme.controlHeight.large,
            borderRadius: `${theme.shape.borderRadius}px`,
            border: `${theme.typography.pxToRem(1)} solid ${
              theme.palette.divider
            }`,
            color: copiedLink
              ? theme.palette.green.main
              : theme.palette.grey[600],
          })}
        >
          {copiedLink ? (
            <CheckIcon sx={{ fontSize: 17 }} />
          ) : (
            <ShareOutlinedIcon sx={{ fontSize: 17 }} />
          )}
        </ButtonBase>
      </Stack>

      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </Stack>
  );
};

export default FeedDetail;
